import { useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { scrollStore } from "@/utils/scrollStore";

export default function ParticleField({ onProgress }) {
  const { scene } = useThree();
  const pointsRef = useRef();

  useEffect(() => {
    onProgress?.(80);
    const count = 2500;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 4 + Math.random() * 16;
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: new THREE.Color("#00F0FF"),
      size: 0.018,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });

    const points = new THREE.Points(geometry, material);
    pointsRef.current = points;
    scene.add(points);
    onProgress?.(88);

    return () => {
      scene.remove(points);
      geometry.dispose();
      material.dispose();
    };
  }, [scene, onProgress]);

  useFrame((state) => {
    if (!pointsRef.current) return;
    const p = scrollStore.progress;
    const ign = scrollStore.igniting;
    // Particles accelerate progressively as user scrolls — like streaming data
    const speed = 0.012 + p * 0.15 + (ign ? 0.4 : 0);
    pointsRef.current.rotation.y = state.clock.elapsedTime * speed;
    pointsRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.005) * 0.08;
    // Pull particles inward slightly on scroll so they form a denser field
    pointsRef.current.scale.setScalar(1 - p * 0.15);
  });

  return null;
}
